import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, ArrowDown, Check, Send } from 'lucide-react';
import { SEVERITIES, type RunnableItem } from '@odr/shared';

import { OfflineBanner } from '../../components/SyncIndicator';
import {
  Button,
  Callout,
  Field,
  Input,
  Modal,
  ProgressBar,
  Select,
  Spinner,
  Textarea,
} from '../../components/ui';
import { useAuth } from '../../lib/auth';
import { ItemCard } from './ItemCard';
import { useRun } from './useRun';

/**
 * Mobile checklist execution (directive §48).
 *
 * Everything about this screen assumes: phone, one hand, possibly gloves,
 * possibly no signal. Answers land locally and instantly; the network is
 * incidental.
 */
export function RunPage() {
  const { runId = '' } = useParams();
  const navigate = useNavigate();
  const user = useAuth((s) => s.user);
  const run = useRun(runId);

  const [problemFor, setProblemFor] = useState<RunnableItem | null>(null);
  const [submitOpen, setSubmitOpen] = useState(false);

  if (run.loading && !run.runnable) return <Spinner label="Opening checklist" />;

  if (run.error) {
    return (
      <div className="p-4">
        <Callout tone="alarm" title="Cannot open this checklist">
          {run.error}
        </Callout>
        <Button className="mt-4" variant="secondary" onClick={() => navigate(-1)}>
          Go back
        </Button>
      </div>
    );
  }

  if (!run.runnable) return <Spinner label="Opening checklist" />;

  const { runnable, progress } = run;
  const closed = ['SUBMITTED', 'VERIFIED', 'CANCELLED'].includes(runnable.run.status);
  const readOnly = closed || runnable.run.operatorId !== user?.id;

  const firstUnanswered = run.items.find((item) => !run.answers.has(item.id));
  const firstUnresolved = progress.unresolved[0];
  const jumpTarget = firstUnresolved ?? firstUnanswered;

  const scrollTo = (item: RunnableItem) => {
    document.getElementById(`item-${item.id}`)?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  return (
    <div className="pb-32">
      <OfflineBanner />

      {/* Sticky context header */}
      <header className="sticky top-14 z-20 border-b border-steel-200 bg-white px-4 py-3">
        <div className="flex items-start gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            aria-label="Back"
            className="-ml-2 rounded-lg p-2 text-steel-600 hover:bg-steel-100"
          >
            <ArrowLeft aria-hidden className="size-5" />
          </button>
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-base font-bold text-steel-900">
              {runnable.template.name}
            </h1>
            <p className="truncate text-sm text-steel-600">
              <Link to={`/machines/${runnable.machine.id}`} className="font-semibold hover:underline">
                {runnable.machine.code}
              </Link>{' '}
              · {runnable.machine.name} · {runnable.machine.lineName}
            </p>
            <p className="text-xs text-steel-400">
              v{runnable.template.versionNo}
              {runnable.run.shift ? ` · Shift ${runnable.run.shift}` : ''}
            </p>
          </div>
        </div>

        <div className="mt-3">
          <ProgressBar
            value={progress.answered}
            total={progress.total}
            tone={progress.abnormal > 0 ? 'warn' : 'info'}
            label="Progress"
          />
        </div>

        {progress.abnormal > 0 ? (
          <p className="mt-2 flex items-center gap-1.5 text-xs font-bold text-alarm-700">
            <AlertTriangle aria-hidden className="size-3.5" />
            {progress.abnormal} abnormal finding{progress.abnormal === 1 ? '' : 's'}
            {progress.unresolved.length > 0
              ? ` · ${progress.unresolved.length} still need follow-up`
              : ' · all followed up'}
          </p>
        ) : null}
      </header>

      {readOnly ? (
        <div className="p-4">
          <Callout tone="info" title="Read only">
            {closed
              ? `This checklist has been ${runnable.run.status.toLowerCase()}. Answers can no longer be changed here.`
              : 'This checklist belongs to another operator.'}
          </Callout>
        </div>
      ) : null}

      {runnable.run.status === 'IN_PROGRESS' && run.fromCache ? (
        <div className="px-4 pt-3">
          <Callout tone="warn">
            Showing the copy saved on this device. Your answers are being kept and will send
            when you are back in coverage.
          </Callout>
        </div>
      ) : null}

      {/* Sections */}
      {runnable.sections.map((section) => (
        <section key={section.id}>
          <h2 className="sticky top-[8.5rem] z-10 border-y border-steel-200 bg-steel-50 px-4 py-2 text-xs font-extrabold tracking-widest text-steel-600 uppercase">
            {section.title}
            {section.description ? (
              <span className="ml-2 font-medium text-steel-400 normal-case">
                {section.description}
              </span>
            ) : null}
          </h2>

          {section.items.map((item) => (
            <ItemCard
              key={item.id}
              item={item}
              answer={run.answers.get(item.id)}
              outstanding={run.outstandingFor(item)}
              tagNo={run.taggedItems.get(item.id)}
              evidenceCount={run.evidenceCount(item.id)}
              readOnly={readOnly}
              onAnswer={(value) => run.answer(item, value)}
              onPhoto={(file) => void run.attachPhoto(item, file, file.name || 'photo.jpg')}
              onRaiseProblem={() => setProblemFor(item)}
            />
          ))}
        </section>
      ))}

      {/* Sticky action bar */}
      {!readOnly ? (
        <div className="fixed inset-x-0 bottom-16 z-20 border-t border-steel-200 bg-white px-4 py-3 safe-bottom lg:bottom-0">
          <div className="mx-auto flex max-w-3xl items-center gap-2">
            {jumpTarget ? (
              <Button
                variant="secondary"
                size="lg"
                icon={ArrowDown}
                onClick={() => scrollTo(jumpTarget)}
                className="shrink-0"
              >
                {firstUnresolved ? 'Next issue' : 'Next task'}
              </Button>
            ) : null}
            <Button
              size="lg"
              fullWidth
              icon={progress.canSubmit ? Send : AlertTriangle}
              variant={progress.canSubmit ? 'success' : 'secondary'}
              onClick={() => setSubmitOpen(true)}
            >
              {progress.canSubmit
                ? 'Submit checklist'
                : progress.requiredRemaining > 0
                  ? `${progress.requiredRemaining} task${progress.requiredRemaining === 1 ? '' : 's'} left`
                  : `${progress.unresolved.length} to follow up`}
            </Button>
          </div>
        </div>
      ) : null}

      {problemFor ? (
        <RaiseProblemModal
          item={problemFor}
          machineCode={runnable.machine.code}
          onClose={() => setProblemFor(null)}
          onSubmit={async (input) => {
            await run.raiseProblem(problemFor, input);
            setProblemFor(null);
          }}
        />
      ) : null}

      <SubmitModal
        open={submitOpen}
        onClose={() => setSubmitOpen(false)}
        canSubmit={progress.canSubmit}
        requiredRemaining={progress.requiredRemaining}
        unresolved={progress.unresolved}
        abnormal={progress.abnormal}
        defaultName={user?.fullName ?? ''}
        onJump={(item) => {
          setSubmitOpen(false);
          setTimeout(() => scrollTo(item), 120);
        }}
        onSubmit={async (notes, signedName) => {
          await run.submit(notes, signedName);
          setSubmitOpen(false);
          navigate('/', { replace: true });
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------

function RaiseProblemModal({
  item,
  machineCode,
  onClose,
  onSubmit,
}: {
  item: RunnableItem;
  machineCode: string;
  onClose: () => void;
  onSubmit: (input: {
    title: string;
    description: string;
    severity: string;
    safetyImpact: boolean;
    qualityImpact: boolean;
    productionImpact: boolean;
  }) => Promise<void>;
}) {
  const [title, setTitle] = useState(`${item.task} — abnormal`);
  const [description, setDescription] = useState('');
  const [severity, setSeverity] = useState(item.defaultSeverity);
  const [safety, setSafety] = useState(Boolean(item.safetyWarning));
  const [quality, setQuality] = useState(Boolean(item.qualityWarning));
  const [production, setProduction] = useState(false);
  const [busy, setBusy] = useState(false);

  return (
    <Modal
      open
      onClose={onClose}
      title="Report a problem"
      footer={
        <div className="flex gap-2">
          <Button variant="secondary" fullWidth onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="danger"
            fullWidth
            loading={busy}
            disabled={description.trim().length < 3}
            onClick={async () => {
              setBusy(true);
              try {
                await onSubmit({
                  title: title.trim(),
                  description: description.trim(),
                  severity,
                  safetyImpact: safety,
                  qualityImpact: quality,
                  productionImpact: production,
                });
              } finally {
                setBusy(false);
              }
            }}
          >
            Raise tag
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <p className="rounded-lg bg-steel-50 p-3 text-sm text-steel-600">
          {machineCode} · task #{item.seq} · {item.task}
          {item.standard ? (
            <>
              <br />
              <span className="text-steel-500">Standard: {item.standard}</span>
            </>
          ) : null}
        </p>

        <Field label="Short title" required>
          <Input value={title} onChange={(event) => setTitle(event.target.value)} maxLength={200} />
        </Field>

        <Field label="What did you see?" required hint="A sentence is enough. Add a photo too if you can.">
          <Textarea
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            rows={4}
            placeholder="e.g. Pressure dropped to 4.2 bar and the gauge is fluctuating."
          />
        </Field>

        <Field label="How serious is it?">
          <Select value={severity} onChange={(event) => setSeverity(event.target.value as never)}>
            {SEVERITIES.map((value) => (
              <option key={value} value={value}>
                {value.charAt(0) + value.slice(1).toLowerCase()}
              </option>
            ))}
          </Select>
        </Field>

        <fieldset>
          <legend className="mb-2 text-sm font-semibold text-steel-800">Impact</legend>
          <div className="space-y-2">
            <Toggle label="Safety risk" checked={safety} onChange={setSafety} />
            <Toggle label="Product quality risk" checked={quality} onChange={setQuality} />
            <Toggle label="Production stopped or slowed" checked={production} onChange={setProduction} />
          </div>
        </fieldset>
      </div>
    </Modal>
  );
}

function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <label className="flex min-h-11 cursor-pointer items-center gap-3 rounded-lg border border-steel-300 px-3">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="size-5 rounded border-steel-400"
      />
      <span className="text-sm font-medium text-steel-800">{label}</span>
    </label>
  );
}

function SubmitModal({
  open,
  onClose,
  canSubmit,
  requiredRemaining,
  unresolved,
  abnormal,
  defaultName,
  onJump,
  onSubmit,
}: {
  open: boolean;
  onClose: () => void;
  canSubmit: boolean;
  requiredRemaining: number;
  unresolved: RunnableItem[];
  abnormal: number;
  defaultName: string;
  onJump: (item: RunnableItem) => void;
  onSubmit: (notes: string | null, signedName: string | null) => Promise<void>;
}) {
  const [notes, setNotes] = useState('');
  const [name, setName] = useState(defaultName);
  const [busy, setBusy] = useState(false);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={canSubmit ? 'Submit this checklist' : 'Not ready to submit'}
      footer={
        canSubmit ? (
          <div className="flex gap-2">
            <Button variant="secondary" fullWidth onClick={onClose}>
              Keep working
            </Button>
            <Button
              variant="success"
              fullWidth
              icon={Check}
              loading={busy}
              disabled={name.trim().length < 2}
              onClick={async () => {
                setBusy(true);
                try {
                  await onSubmit(notes.trim() || null, name.trim());
                } finally {
                  setBusy(false);
                }
              }}
            >
              Submit
            </Button>
          </div>
        ) : (
          <Button fullWidth onClick={onClose}>
            Back to checklist
          </Button>
        )
      }
    >
      {canSubmit ? (
        <div className="space-y-4">
          {abnormal > 0 ? (
            <Callout tone="warn" title={`${abnormal} abnormal finding${abnormal === 1 ? '' : 's'}`}>
              These have been followed up and will go to your supervisor for review.
            </Callout>
          ) : (
            <Callout tone="ok" title="No abnormalities found">
              Everything on this machine met the standard.
            </Callout>
          )}

          <Field label="Anything else to note?" hint="Optional — anything the next shift should know.">
            <Textarea value={notes} onChange={(event) => setNotes(event.target.value)} rows={3} />
          </Field>

          <Field
            label="Your name"
            required
            hint="Signing confirms you performed these checks and the results are accurate."
          >
            <Input value={name} onChange={(event) => setName(event.target.value)} />
          </Field>
        </div>
      ) : (
        <div className="space-y-4">
          {requiredRemaining > 0 ? (
            <Callout tone="warn" title={`${requiredRemaining} task${requiredRemaining === 1 ? '' : 's'} not answered`}>
              Every required task has to be answered before you can submit.
            </Callout>
          ) : null}

          {unresolved.length > 0 ? (
            <div>
              <p className="mb-2 text-sm font-semibold text-steel-800">
                These findings still need follow-up:
              </p>
              <ul className="divide-y divide-steel-100 rounded-lg border border-steel-200">
                {unresolved.map((item) => (
                  <li key={item.id}>
                    <button
                      type="button"
                      onClick={() => onJump(item)}
                      className="flex w-full items-center justify-between gap-2 px-3 py-3 text-left hover:bg-steel-50"
                    >
                      <span className="min-w-0">
                        <span className="block text-sm font-semibold text-steel-900">
                          #{item.seq} {item.task}
                        </span>
                      </span>
                      <span className="shrink-0 text-xs font-bold text-info-700">Go →</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      )}
    </Modal>
  );
}
